import { useState } from 'react';

interface LipShade {
  id: string;
  name: string;
  subtitle: string;
  hex: string;
  rgb: string;
  overlayOpacity: number;
}

const shades: LipShade[] = [
  {
    id: 'rouge-instinct',
    name: 'Rouge Instinct',
    subtitle: 'The beast awakens',
    hex: '#7A1A18',
    rgb: '122,26,24',
    overlayOpacity: 0.72,
  },
  {
    id: 'velvet-noir',
    name: 'Velvet Noir',
    subtitle: 'Darkness, refined',
    hex: '#3D0F1A',
    rgb: '61,15,26',
    overlayOpacity: 0.78,
  },
  {
    id: 'nude-sauvage',
    name: 'Nude Sauvage',
    subtitle: 'Bare and untamed',
    hex: '#C4856A',
    rgb: '196,133,106',
    overlayOpacity: 0.65,
  },
  {
    id: 'terra-feline',
    name: 'Terra Feline',
    subtitle: 'Earth beneath the paw',
    hex: '#8B4A3A',
    rgb: '139,74,58',
    overlayOpacity: 0.70,
  },
  {
    id: 'crimson-wild',
    name: 'Crimson Wild',
    subtitle: 'Unfiltered desire',
    hex: '#A01020',
    rgb: '160,16,32',
    overlayOpacity: 0.75,
  },
  {
    id: 'rose-predator',
    name: 'Rose Predator',
    subtitle: 'Soft on the surface',
    hex: '#C4607A',
    rgb: '196,96,122',
    overlayOpacity: 0.62,
  },
];

export default function LipstickTryOn() {
  const [selected, setSelected] = useState<LipShade>(shades[0]);
  const [hovered, setHovered] = useState<string | null>(null);

  const displayShade = hovered ? shades.find((s) => s.id === hovered) ?? selected : selected;

  return (
    <section className="bg-black py-28 px-6 md:px-16 border-t border-[#EADDCC]/8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p
            className="text-[#4D1815] text-xs mb-4"
            style={{ letterSpacing: '0.5em', fontFamily: "'Cormorant Garamond', serif" }}
          >
            — VIRTUAL TRY-ON —
          </p>
          <h2
            className="text-[#EADDCC] font-light"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(28px, 4vw, 52px)',
              letterSpacing: '0.1em',
            }}
          >
            Find Your Shade
          </h2>
          <p
            className="text-[#EADDCC]/35 text-sm italic mt-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.08em' }}
          >
            Hover to preview · Click to select
          </p>
        </div>

        {/* Main layout */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

          {/* Left — shade selector */}
          <div className="flex flex-col gap-3 w-full lg:w-64 flex-shrink-0">
            {shades.map((shade) => {
              const isActive = selected.id === shade.id;
              return (
                <button
                  key={shade.id}
                  onClick={() => setSelected(shade)}
                  onMouseEnter={() => setHovered(shade.id)}
                  onMouseLeave={() => setHovered(null)}
                  className="group flex items-center gap-4 px-4 py-3 border transition-all duration-400 cursor-pointer whitespace-nowrap text-left"
                  style={{
                    borderColor: isActive ? `${shade.hex}` : 'rgba(234,221,204,0.12)',
                    background: isActive ? `rgba(${shade.rgb},0.12)` : 'transparent',
                  }}
                >
                  {/* Swatch */}
                  <div
                    className="w-8 h-8 flex-shrink-0 rounded-full transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: shade.hex }}
                  />
                  {/* Text */}
                  <div className="flex flex-col">
                    <span
                      className="text-xs font-light transition-colors duration-300"
                      style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        letterSpacing: '0.15em',
                        color: isActive ? '#EADDCC' : 'rgba(234,221,204,0.55)',
                      }}
                    >
                      {shade.name.toUpperCase()}
                    </span>
                    <span
                      className="text-xs italic mt-0.5 transition-colors duration-300"
                      style={{
                        fontFamily: "'Cormorant Garamond', serif",
                        color: isActive ? 'rgba(234,221,204,0.5)' : 'rgba(234,221,204,0.25)',
                        fontSize: '11px',
                      }}
                    >
                      {shade.subtitle}
                    </span>
                  </div>
                  {/* Active indicator */}
                  {isActive && (
                    <div className="ml-auto w-1 h-6 flex-shrink-0" style={{ backgroundColor: shade.hex }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Center — lip image with color overlay */}
          <div className="relative flex-1 flex items-center justify-center">
            <div
              className="relative overflow-hidden"
              style={{ width: '480px', height: '480px', maxWidth: '100%' }}
            >
              {/* Base lip image */}
              <img
                src="https://public.readdy.ai/ai/img_res/96bd94e8bf1c946168b3be8deb8a7628.jpg"
                alt="Lip try-on"
                className="w-full h-full object-cover object-center"
                style={{ filter: 'grayscale(15%) contrast(1.05)' }}
              />

              {/* Color overlay — multiply blend */}
              <div
                className="absolute inset-0 transition-all duration-700"
                style={{
                  backgroundColor: displayShade.hex,
                  opacity: displayShade.overlayOpacity,
                  mixBlendMode: 'multiply',
                }}
              />

              {/* Subtle gloss highlight */}
              <div
                className="absolute transition-opacity duration-700"
                style={{
                  top: '38%',
                  left: '30%',
                  width: '40%',
                  height: '12%',
                  background: 'radial-gradient(ellipse, rgba(255,255,255,0.18) 0%, transparent 70%)',
                  borderRadius: '50%',
                  transform: 'rotate(-8deg)',
                }}
              />

              {/* Corner decoration */}
              <div className="absolute top-4 left-4 w-6 h-6 border-t border-l" style={{ borderColor: 'rgba(234,221,204,0.25)' }} />
              <div className="absolute top-4 right-4 w-6 h-6 border-t border-r" style={{ borderColor: 'rgba(234,221,204,0.25)' }} />
              <div className="absolute bottom-4 left-4 w-6 h-6 border-b border-l" style={{ borderColor: 'rgba(234,221,204,0.25)' }} />
              <div className="absolute bottom-4 right-4 w-6 h-6 border-b border-r" style={{ borderColor: 'rgba(234,221,204,0.25)' }} />
            </div>
          </div>

          {/* Right — selected shade info */}
          <div className="flex flex-col gap-6 w-full lg:w-64 flex-shrink-0 items-start lg:items-end text-left lg:text-right">
            {/* Color swatch large */}
            <div
              className="w-16 h-16 rounded-full transition-all duration-700"
              style={{ backgroundColor: displayShade.hex, boxShadow: `0 0 32px ${displayShade.hex}55` }}
            />

            {/* Shade name */}
            <div>
              <p
                className="text-[#EADDCC]/40 text-xs mb-2 transition-all duration-500"
                style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.4em' }}
              >
                SELECTED SHADE
              </p>
              <h3
                className="text-[#EADDCC] font-light transition-all duration-500"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: 'clamp(22px, 2.5vw, 32px)',
                  letterSpacing: '0.08em',
                  lineHeight: 1.2,
                }}
              >
                {displayShade.name}
              </h3>
              <p
                className="text-[#EADDCC]/40 text-sm italic mt-2 transition-all duration-500"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {displayShade.subtitle}
              </p>
            </div>

            {/* Hex code */}
            <div className="flex items-center gap-3 lg:flex-row-reverse">
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: displayShade.hex }}
              />
              <span
                className="text-[#EADDCC]/25 text-xs"
                style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.2em' }}
              >
                {displayShade.hex.toUpperCase()}
              </span>
            </div>

            {/* Divider */}
            <div className="w-12 h-px" style={{ backgroundColor: displayShade.hex }} />

            {/* CTA */}
            <button
              className="group relative px-8 py-3 border text-xs tracking-widest overflow-hidden cursor-pointer whitespace-nowrap transition-all duration-500"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                letterSpacing: '0.3em',
                borderColor: `${displayShade.hex}80`,
                color: 'rgba(234,221,204,0.7)',
              }}
            >
              <span
                className="absolute inset-0 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"
                style={{ backgroundColor: displayShade.hex }}
              />
              <span className="relative group-hover:text-[#EADDCC] transition-colors duration-500">
                EXPLORE SHADE
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
